import posthog from 'posthog-js'
import type { UtmParams } from '@/lib/utm'

// Safe wrapper — no-ops on server
function isClient() {
  return typeof window !== 'undefined'
}

export function captureEvent(event: string, properties?: Record<string, unknown>) {
  if (!isClient()) return
  posthog.capture(event, properties)
}

export function identifyUser(
  userId: string,
  properties?: {
    email?: string
    persona?: string
    comfort_level?: number
    subscription_tier?: string
  } & UtmParams
) {
  if (!isClient()) return
  posthog.identify(userId, properties)
}

export function resetUser() {
  if (!isClient()) return
  posthog.reset()
}

// Typed event helpers
export const analytics = {
  signupCompleted: (method: 'magic_link' | 'google', utmParams?: UtmParams) =>
    captureEvent('signup_completed', {
      method,
      // `cta` matches PostHog dashboard filters (google_oauth | magic_link_sent)
      cta: method === 'google' ? 'google_oauth' : 'magic_link_sent',
      ...utmParams,
    }),

  onboardingCompleted: (properties: {
    persona: string
    comfort_level: number
    interests: string[]
  }) => captureEvent('onboarding_completed', properties),

  sessionStarted: (properties: { tool_slug: string; session_id: string }) =>
    captureEvent('session_started', properties),

  sessionCompleted: (properties: { session_id: string; message_count?: number }) =>
    captureEvent('session_completed', properties),

  upgradeInitiated: (plan: 'normal' | 'pro', pricing_variant?: string) =>
    captureEvent('upgrade_initiated', { plan, ...(pricing_variant && { pricing_variant }) }),

  pricingPageViewed: (properties: { variant: string }) =>
    captureEvent('pricing_page_viewed', properties),

  checkoutCompleted: (properties: { plan: string }) =>
    captureEvent('checkout_completed', properties),

  guideViewed: (properties: { slug: string; title?: string }) =>
    captureEvent('guide_viewed', properties),

  ctaClicked: (properties: { location: string; cta: string; persona?: string }) =>
    captureEvent('cta_clicked', properties),

  newsletterSubscribed: (properties: { source: string }) =>
    captureEvent('newsletter_subscribed', properties),
}
